import React, { createContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { validateEmail, validatePassword } from "../utils/helpers";
import {
  GET_ADMIN_USERS_API_URL,
  GET_USER_INFO_API_URL,
  LOGIN_API_URL,
  REGISTER_API_URL,
  UPDATE_ADMIN_USER_API_URL,
  UPDATE_USER_INFO_API_URL,
} from "../config/endPoints";
import { message } from "antd";
import axios from "axios";
import setAuthToken from "../utils/setAuthToken";
import jwtDecode from "jwt-decode";

const AuthContext = createContext();

const AuthenticationContext = ({ children }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userInfo, setUserInfo] = useState({});
  const [updateUserInfo, setUpdateUserInfo] = useState(userInfo);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUser, setAdminUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    image: [],
    userId: userInfo?._id,
  });

  const token = localStorage.getItem("token");

  const login = (data) => {
    const { email, password } = data;

    if (!validateEmail(email)) {
      message.error("Please enter a valid email address.");
      return;
    }

    if (!validatePassword(password)) {
      message.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    axios
      .post(LOGIN_API_URL, data)
      .then((res) => {
        const token = res.data.token;
        if (token) {
          setAuthToken(token);
          localStorage.setItem("token", token);
          navigate(
            res?.data?.userType === "admin" ? "/admin-dashboard" : "/dashboard"
          );
          fetchUserInfo();
        }
        message.success(res.data.message);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.response.data.error);
      });
  };

  const fetchUserInfo = async (token) => {
    try {
      if (token) {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          logout();
          return;
        }
        setAuthToken(token);
        const res = await axios.get(GET_USER_INFO_API_URL);
        const data = res?.data?.user;
        setUserInfo(data);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const logout = () => {
    setAuthToken();
    localStorage.removeItem("token");
    navigate("/");
  };

  const updateUser = (data) => {
    const { email, password, imageUrl } = data;

    if (email && !validateEmail(email)) {
      message.error("Please enter a valid email address.");
      return;
    }

    if (password && !validatePassword(password)) {
      message.error("Password must be at least 8 characters.");
      return;
    }
    if (password && data.password !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }
    setLoading(true);
    axios
      .patch(UPDATE_USER_INFO_API_URL, data)
      .then((res) => {
        message.success(
          imageUrl
            ? "Your Profile Image Uploaded Successfully."
            : res.data.message
        );
        fetchUserInfo(token);
        setEditing(false);
        setUpdateUserInfo({});
        setConfirmPassword("");
      })
      .catch((err) => {
        setLoading(false);
        message.error(
          imageUrl
            ? "Error uploading image, please try again!"
            : err.response.data.error
        );
        console.log(err);
      });
  };

  const cancelUpdate = () => {
    fetchUserInfo(token);
    setUpdateUserInfo({});
    setEditing(false);
  };

  const getAdminUsers = async (token) => {
    try {
      if (token) {
        setAuthToken(token);
        const res = await axios.get(GET_ADMIN_USERS_API_URL);
        setAdminUsers(res?.data?.adminUsers);
      }
    } catch (err) {
      message.error(err?.response?.data?.error);
    }
  };

  const updateAUser = (updateAltUser, sUid) => {
    const { email, password, image } = updateAltUser;

    if (email && !validateEmail(email)) {
      message.error("Please enter a valid email address.");
      return;
    }

    if (password && !validatePassword(password)) {
      message.error("Password must be at least 8 characters.");
      return;
    }

    const formData = new FormData();

    for (const key in updateAltUser) {
      if (key === "image") {
        if (Array.isArray(image) && image.length === 1) {
          formData.append("images", image[0]);
        } else {
          message.error("Please provide a single image file.");
          return;
        }
      } else {
        formData.append(key, updateAltUser[key]);
      }
    }
    setLoading(true);
    axios
      .patch(`${UPDATE_ADMIN_USER_API_URL}/${sUid}`, formData)
      .then((res) => {
        message.success(res.data.message);
        setTimeout(() => {
          const cancelButton = document.querySelector(
            "#editUserModal .btn-light"
          );
          if (cancelButton) {
            cancelButton.click();
          }
          setLoading(false);
          getAdminUsers(token);
        }, 500);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.response.data.error);
      });
  };

  const addAdminUser = (data) => {
    const { firstName, lastName, email, password, image } = data;

    if (!firstName || !lastName || !email || !password) {
      message.error("Please fill in all fields.");
      return;
    }

    if (!validateEmail(email)) {
      message.error("Please enter a valid email address.");
      return;
    }

    if (!validatePassword(password)) {
      message.error("Password must be at least 8 characters.");
      return;
    }

    const formData = new FormData();

    for (const key in data) {
      if (key === "image") {
        if (image && Array.isArray(image) && image.length > 1) {
          message.error("Please provide a single image file.");
          return;
        }
        if (image && image.length === 1) {
          formData.append("images", image[0]);
        }
      } else {
        formData.append(key, data[key]);
      }
    }

    setLoading(true);
    axios
      .post(REGISTER_API_URL, formData)
      .then((res) => {
        message.success(res.data.message);
        setAdminUser({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          image: [],
          userId: userInfo?._id,
        });
        setTimeout(() => {
          const cancelButton = document.querySelector(".btn-light");
          if (cancelButton) {
            cancelButton.click();
          }
          setLoading(false);
          getAdminUsers(token);
        }, 500);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.response.data.error);
        console.log(err);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        loading,
        setLoading,
        confirmPassword,
        setConfirmPassword,
        userInfo,
        fetchUserInfo,
        logout,
        updateUser,
        updateUserInfo,
        setUpdateUserInfo,
        cancelUpdate,
        editing,
        setEditing,
        getAdminUsers,
        adminUsers,
        updateAUser,
        addAdminUser,
        adminUser,
        setAdminUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthenticationContext, AuthContext };
